import { tokens } from "./lib/api.js";
import { loginView } from "./routes/login.js";
import { recordingsView } from "./routes/recordings.js";
import { recordingView } from "./routes/recording.js";
import { clipsView } from "./routes/clips.js";
import { accountView } from "./routes/account.js";
import { verifyView, expiringView } from "./routes/account-extra.js";
import { challengesView, playersView, profileView } from "./routes/community.js";
import { requestsView } from "./routes/requests.js";

import { submitView } from "./routes/submit.js";
import { extCheckoutView } from "./routes/ext-checkout.js";
import { forgotView, resetView } from "./routes/password.js";

import { scheduleView } from "./routes/schedule.js";

const app = document.getElementById("app")!;
const nav = document.getElementById("nav")!;
const appfoot = document.getElementById("appfoot")!;
const appbar = document.getElementById("appbar")!;
const userbox = document.getElementById("userbox")!;

const LINKS = [
  ["#/recordings", "Recordings"],
  ["#/clips", "Clips"],

  ["#/schedule", "Schedule"],
  ["#/requests", "Paid videos"],
  ["#/expiring", "Expiring"],
  ["#/players", "Players"],
  ["#/challenges", "Challenges"],
  ["#/profile", "Profile"],
  ["#/account", "Account"],
] as const;

/** Signed-out surfaces (auth, verification) own the whole viewport. */
function hideChrome() {
  appbar.hidden = true;
  appfoot.hidden = true;
  nav.innerHTML = "";
  userbox.innerHTML = "";
}

function renderNav(active: string) {
  appbar.hidden = false;
  appfoot.hidden = false;
  nav.innerHTML = LINKS.map(
    ([href, label]) =>
      `<a href="${href}" class="${active.startsWith(href) ? "on" : ""}">${label}</a>`,
  ).join("");

  userbox.innerHTML = `<button class="btn small" id="out">Sign out</button>`;
  userbox.querySelector("#out")!.addEventListener("click", () => {
    tokens.clear();
    location.hash = "";
    route();
  });
}

async function route() {
  const hash = location.hash || "#/recordings";

  // Verification runs before the auth check: the link is often opened on a
  // different device from the one the account was created on, and forcing a
  // sign-in first turns a single click into a password reset.
  // `app=streamnexa` (after the token) marks a StreamNexa sign-up, whose
  // confirmation leads back to StreamNexa rather than to Studio.
  const verify = hash.match(/^#\/verify\?(.+)$/);
  if (verify) {
    hideChrome();
    const q = new URLSearchParams(verify[1]!);
    return verifyView(app, q.get("token") ?? "", q.get("app") === "streamnexa");
  }

  // The person who paid to have their video reacted to. They have no account
  // and never will, so this runs before the auth check -- the token in their
  // emailed link is the whole credential.
  const submit = hash.match(/^#\/submit\?t=(.+)$/);
  if (submit) {
    hideChrome();
    return submitView(app, decodeURIComponent(submit[1]!));
  }

  // Password reset: by definition signed out.
  if (hash.startsWith("#/forgot")) {
    hideChrome();
    return forgotView(app);
  }
  const reset = hash.match(/^#\/reset\?token=(.+)$/);
  if (reset) {
    hideChrome();
    return resetView(app, decodeURIComponent(reset[1]!));
  }

  // The Chrome extension's checkout. Signed out is fine: the server created
  // the transaction for the account that asked, and this page only opens it.
  const checkout = hash.match(/^#\/checkout\?txn=(.+)$/);
  if (checkout) {
    hideChrome();
    return extCheckoutView(app, decodeURIComponent(checkout[1]!));
  }

  if (!tokens.access) {
    hideChrome();
    return loginView(app, () => (location.hash = "#/recordings"));
  }

  renderNav(hash);

  // A view that throws should say so rather than leaving a blank page.
  try {
    const detail = hash.match(/^#\/recordings\/(.+)$/);
    if (detail) return await recordingView(app, detail[1]!);
    if (hash.startsWith("#/clips")) return await clipsView(app);
    if (hash.startsWith("#/requests")) return await requestsView(app);

    if (hash.startsWith("#/schedule")) return await scheduleView(app);
    if (hash.startsWith("#/expiring")) return await expiringView(app);
    if (hash.startsWith("#/players")) return await playersView(app);
    if (hash.startsWith("#/challenges")) return await challengesView(app);
    if (hash.startsWith("#/profile")) return await profileView(app);
    // Settings was folded into Account when pairing replaced raw device
    // tokens; keep old links working rather than silently sending them to
    // Recordings.
    if (hash.startsWith("#/account") || hash.startsWith("#/settings")) {
      return await accountView(app);
    }
    return await recordingsView(app);
  } catch (err) {
    app.innerHTML = `<div class="notice warn"><strong>Couldn't load this page.</strong>
      ${err instanceof Error ? err.message : String(err)}</div>`;
  }
}

window.addEventListener("hashchange", () => void route());
void route();
